import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './CakeSettings.css';

const defaultSettings = {
  shop_open_time: '09:00',
  shop_close_time: '22:00',
  delivery_enabled: true,
  pickup_enabled: true,
  delivery_start_time: '10:00',
  delivery_end_time: '21:30',
  regular_prep_minutes: 45,
  custom_orders_enabled: true,
  custom_advance_hours: 24,
  same_day_custom_enabled: false,
  custom_delivery_start_time: '11:00',
  custom_delivery_end_time: '21:00',
};

const normalizeTime = (value, fallback) => {
  const text = String(value || fallback || '');
  return text ? text.slice(0, 5) : '';
};

const CakeSettings = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const token = () => localStorage.getItem('cakeToken') || '';

  useEffect(() => {
    const load = async () => {
      try {
        const auth = token();
        if (!auth) {
          navigate('/cake/login');
          return;
        }

        const response = await fetch('/api/cake/products/settings', {
          headers: {
            Authorization: `Bearer ${auth}`,
          },
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Settings load nahi hui');
        }

        const s = data.settings || {};

        setForm({
          shop_open_time: normalizeTime(s.shop_open_time, '09:00'),
          shop_close_time: normalizeTime(s.shop_close_time, '22:00'),
          delivery_enabled: Boolean(Number(s.delivery_enabled)),
          pickup_enabled: Boolean(Number(s.pickup_enabled)),
          delivery_start_time: normalizeTime(s.delivery_start_time, '10:00'),
          delivery_end_time: normalizeTime(s.delivery_end_time, '21:30'),
          regular_prep_minutes: Number(s.regular_prep_minutes || 45),
          custom_orders_enabled: Boolean(Number(s.custom_orders_enabled)),
          custom_advance_hours: Number(s.custom_advance_hours || 24),
          same_day_custom_enabled: Boolean(Number(s.same_day_custom_enabled)),
          custom_delivery_start_time: normalizeTime(
            s.custom_delivery_start_time,
            '11:00'
          ),
          custom_delivery_end_time: normalizeTime(
            s.custom_delivery_end_time,
            '21:00'
          ),
        });
      } catch (error) {
        setMessage(error.message || 'Settings load nahi hui');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [navigate]);

  const setField = (name, value) => {
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const save = async () => {
    try {
      setSaving(true);
      setMessage('');

      const response = await fetch('/api/cake/products/settings', {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(form),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Settings save nahi hui');
      }

      setMessage('Shop settings saved successfully.');
    } catch (error) {
      setMessage(error.message || 'Settings save nahi hui');
    } finally {
      setSaving(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('cakeToken');
    localStorage.removeItem('cakeVendor');
    navigate('/cake/login');
  };

  return (
    <div className="cake-settings-page">
      <aside className="cake-settings-sidebar">
        <div className="cake-settings-brand">
          <div className="cake-settings-brand-icon">🎂</div>
          <div>
            <strong>VAKRA CAKE</strong>
            <small>Vendor Panel</small>
          </div>
        </div>

        <nav>
          <button onClick={() => navigate('/cake/vendor')}>🏠 Home</button>
          <button onClick={() => navigate('/cake/cakes')}>🎂 Cakes / Products</button>
          <button onClick={() => navigate('/cake/orders')}>📦 Orders</button>
          <button className="active">⚙️ Settings</button>
        </nav>

        <button className="cake-settings-logout" onClick={logout}>
          ↪ Logout
        </button>
      </aside>

      <main className="cake-settings-main">
        <header className="cake-settings-header">
          <div>
            <span>SHOP CONFIGURATION</span>
            <h1>Order & Delivery Settings</h1>
            <p>
              Ye settings customer page par automatically apply hongi.
            </p>
          </div>

          <button
            className="cake-settings-save top"
            type="button"
            disabled={saving || loading}
            onClick={save}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </header>

        {message && <div className="cake-settings-message">{message}</div>}

        {loading ? (
          <div className="cake-settings-loading">Loading settings...</div>
        ) : (
          <div className="cake-settings-grid">
            <section className="cake-settings-card">
              <div className="cake-settings-card-title">
                <div className="icon">🕒</div>
                <div>
                  <h2>Shop Timing</h2>
                  <p>Customer ko shop ki actual timing dikhai jayegi.</p>
                </div>
              </div>

              <div className="cake-settings-two">
                <label>
                  Opening Time
                  <input
                    type="time"
                    value={form.shop_open_time}
                    onChange={(e) => setField('shop_open_time', e.target.value)}
                  />
                </label>

                <label>
                  Closing Time
                  <input
                    type="time"
                    value={form.shop_close_time}
                    onChange={(e) => setField('shop_close_time', e.target.value)}
                  />
                </label>
              </div>

              <label>
                Regular Order Preparation Time
                <select
                  value={form.regular_prep_minutes}
                  onChange={(e) =>
                    setField('regular_prep_minutes', Number(e.target.value))
                  }
                >
                  {[20, 30, 45, 60, 90, 120].map((value) => (
                    <option key={value} value={value}>
                      {value} minutes
                    </option>
                  ))}
                </select>
              </label>
            </section>

            <section className="cake-settings-card">
              <div className="cake-settings-card-title">
                <div className="icon">🛵</div>
                <div>
                  <h2>Delivery & Pickup</h2>
                  <p>Vendor apne fulfilment options control kar sakta hai.</p>
                </div>
              </div>

              <div className="cake-settings-toggles">
                <label className="cake-settings-toggle">
                  <input
                    type="checkbox"
                    checked={form.delivery_enabled}
                    onChange={(e) =>
                      setField('delivery_enabled', e.target.checked)
                    }
                  />
                  <span />
                  Delivery Available
                </label>

                <label className="cake-settings-toggle">
                  <input
                    type="checkbox"
                    checked={form.pickup_enabled}
                    onChange={(e) => setField('pickup_enabled', e.target.checked)}
                  />
                  <span />
                  Pickup Available
                </label>
              </div>

              <div className="cake-settings-two">
                <label>
                  Delivery Start
                  <input
                    type="time"
                    value={form.delivery_start_time}
                    disabled={!form.delivery_enabled}
                    onChange={(e) =>
                      setField('delivery_start_time', e.target.value)
                    }
                  />
                </label>

                <label>
                  Delivery End
                  <input
                    type="time"
                    value={form.delivery_end_time}
                    disabled={!form.delivery_enabled}
                    onChange={(e) =>
                      setField('delivery_end_time', e.target.value)
                    }
                  />
                </label>
              </div>
            </section>

            <section className="cake-settings-card wide custom-card">
              <div className="cake-settings-card-title">
                <div className="icon">✨</div>
                <div>
                  <h2>Custom Cake Rules</h2>
                  <p>
                    Customer ko custom cake order karne se pehle ye rules dikhte
                    hain.
                  </p>
                </div>
              </div>

              <div className="cake-settings-toggles">
                <label className="cake-settings-toggle">
                  <input
                    type="checkbox"
                    checked={form.custom_orders_enabled}
                    onChange={(e) =>
                      setField('custom_orders_enabled', e.target.checked)
                    }
                  />
                  <span />
                  Accept Custom Cake Orders
                </label>

                <label className="cake-settings-toggle">
                  <input
                    type="checkbox"
                    checked={form.same_day_custom_enabled}
                    disabled={!form.custom_orders_enabled}
                    onChange={(e) =>
                      setField('same_day_custom_enabled', e.target.checked)
                    }
                  />
                  <span />
                  Same Day Custom Cake
                </label>
              </div>

              <div className="cake-settings-three">
                <label>
                  Minimum Advance Time
                  <select
                    value={form.custom_advance_hours}
                    disabled={!form.custom_orders_enabled}
                    onChange={(e) =>
                      setField('custom_advance_hours', Number(e.target.value))
                    }
                  >
                    {[3, 6, 12, 24, 36, 48, 72, 96].map((value) => (
                      <option key={value} value={value}>
                        {value} Hours
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  Custom Delivery Start
                  <input
                    type="time"
                    value={form.custom_delivery_start_time}
                    disabled={!form.custom_orders_enabled}
                    onChange={(e) =>
                      setField('custom_delivery_start_time', e.target.value)
                    }
                  />
                </label>

                <label>
                  Custom Delivery End
                  <input
                    type="time"
                    value={form.custom_delivery_end_time}
                    disabled={!form.custom_orders_enabled}
                    onChange={(e) =>
                      setField('custom_delivery_end_time', e.target.value)
                    }
                  />
                </label>
              </div>

              <div className="cake-settings-preview">
                <strong>Customer ko ye rule dikhega:</strong>
                <span>
                  Custom cakes require minimum {form.custom_advance_hours} hours
                  advance order.
                </span>
              </div>
            </section>
          </div>
        )}

        <button
          className="cake-settings-save mobile-save"
          type="button"
          disabled={saving || loading}
          onClick={save}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </main>
    </div>
  );
};

export default CakeSettings;
